// Spotify API integration
// This module handles Spotify playlist analytics and data fetching

import { spotifyConfig } from "../../../config/api.config";
import {
  OutreachCategory,
  type SpotifyPlaylist,
  type SpotifyCategory,
  type SpotifyPlaylistSearchResult,
  type SpotifyPlaylistDetails,
} from "../../../types/outreach";
import { getOutreachItem, updateOutreachItem } from "../firestore/crud";

const SPOTIFY_API_BASE_URL = "https://api.spotify.com/v1";
const CATEGORY_CACHE_TTL_MS = 1000 * 60 * 60; // 1 hour

interface TokenCacheEntry {
  token: string;
  expiresAt: number;
  clientId: string;
  clientSecret: string;
}

interface CategoryCacheEntry {
  timestamp: number;
  categories: SpotifyCategory[];
}

interface SpotifyRequestOptions extends RequestInit {
  clientId?: string;
  clientSecret?: string;
}

let tokenCache: TokenCacheEntry | null = null;
let categoryCache: CategoryCacheEntry | null = null;

const encodeClientCredentials = (
  clientId: string,
  clientSecret: string
): string => {
  const credentials = `${clientId}:${clientSecret}`;
  const globalAny = globalThis as unknown as {
    Buffer?: {
      from: (
        input: string,
        encoding?: string
      ) => { toString: (encoding: string) => string };
    };
    btoa?: (data: string) => string;
  };

  if (globalAny.Buffer?.from) {
    return globalAny.Buffer.from(credentials, "utf-8").toString("base64");
  }

  if (typeof globalAny.btoa === "function") {
    return globalAny.btoa(credentials);
  }

  throw new Error("Unable to encode Spotify credentials to Base64");
};

/**
 * Extract Spotify playlist ID from URL
 *
 * @param url Spotify playlist URL
 * @returns Playlist ID or null if not found
 */
export const extractPlaylistId = (url: string): string | null => {
  try {
    if (!url) {
      return null;
    }

    let normalizedUrl = url.trim();
    if (!normalizedUrl.startsWith("http")) {
      normalizedUrl = `https://${normalizedUrl}`;
    }

    const urlObj = new URL(normalizedUrl);
    const segments = urlObj.pathname.split("/").filter(Boolean);

    const playlistIndex = segments.findIndex(
      (segment) => segment === "playlist"
    );
    if (playlistIndex >= 0 && segments[playlistIndex + 1]) {
      return segments[playlistIndex + 1].split("?")[0];
    }

    return null;
  } catch (error) {
    console.error("Error extracting Spotify playlist ID:", error);
    return null;
  }
};

const getSpotifyAccessToken = async (
  clientId: string = spotifyConfig.clientId,
  clientSecret: string = spotifyConfig.clientSecret
): Promise<string> => {
  if (!clientId || !clientSecret) {
    throw new Error("Spotify client credentials are not configured");
  }

  const now = Date.now();

  if (
    tokenCache &&
    tokenCache.clientId === clientId &&
    tokenCache.clientSecret === clientSecret &&
    now < tokenCache.expiresAt
  ) {
    return tokenCache.token;
  }

  const encodedCredentials = encodeClientCredentials(clientId, clientSecret);
  const response = await fetch("https://accounts.spotify.com/api/token", {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: `Basic ${encodedCredentials}`,
    },
    body: new URLSearchParams({
      grant_type: "client_credentials",
    }),
  });

  if (!response.ok) {
    tokenCache = null;
    const body = await response.json().catch(() => ({}));
    throw new Error(
      body?.error_description || "Unable to obtain Spotify access token"
    );
  }

  const data: { access_token: string; expires_in: number } =
    await response.json();

  const expiresAt = now + Math.max(0, data.expires_in - 60) * 1000;
  tokenCache = {
    token: data.access_token,
    expiresAt,
    clientId,
    clientSecret,
  };

  return data.access_token;
};

const spotifyFetch = async <T>(
  endpoint: string,
  options: SpotifyRequestOptions = {}
): Promise<T> => {
  const { clientId, clientSecret, headers, ...fetchOptions } = options;
  const executeRequest = async (accessToken: string): Promise<Response> =>
    fetch(`${SPOTIFY_API_BASE_URL}${endpoint}`, {
      ...fetchOptions,
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
        ...(headers ?? {}),
      },
    });

  let token = await getSpotifyAccessToken(clientId, clientSecret);
  let response = await executeRequest(token);

  if (response.status === 401) {
    tokenCache = null;
    token = await getSpotifyAccessToken(clientId, clientSecret);
    response = await executeRequest(token);
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null);
    const message =
      (errorBody && (errorBody.error?.message || errorBody.message)) ||
      `Spotify API request failed with status ${response.status}`;
    throw new Error(message);
  }

  return (await response.json()) as T;
};

/**
 * Fetch Spotify browse categories
 */
export const fetchSpotifyCategories = async (
  options?: SpotifyRequestOptions
): Promise<SpotifyCategory[]> => {
  const now = Date.now();
  if (categoryCache && now - categoryCache.timestamp < CATEGORY_CACHE_TTL_MS) {
    return categoryCache.categories;
  }

  try {
    const data = await spotifyFetch<{
      categories: { items: SpotifyCategory[] };
    }>(`/browse/categories?country=US&locale=en_US&limit=50`, options);

    const categories = data.categories?.items ?? [];
    categoryCache = {
      timestamp: now,
      categories,
    };

    return categories;
  } catch (error) {
    console.error("Error fetching Spotify categories:", error);
    throw error;
  }
};

/**
 * Search Spotify playlists by query string
 */
export const searchSpotifyPlaylists = async (
  query: string,
  limit = 50,
  offset = 0,
  options?: SpotifyRequestOptions
): Promise<SpotifyPlaylistSearchResult[]> => {
  try {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      return [];
    }

    const cappedLimit = Math.min(Math.max(limit, 1), 50);
    const cappedOffset = Math.max(offset, 0);
    const params = new URLSearchParams({
      q: trimmedQuery,
      type: "playlist",
      limit: cappedLimit.toString(),
      offset: cappedOffset.toString(),
      market: "US",
    });

    const response = await spotifyFetch<{
      playlists?: { items?: Array<SpotifyPlaylistSearchResult | null> };
    }>(`/search?${params.toString()}`, options);

    const playlists = (response.playlists?.items ?? []).filter(
      (playlist): playlist is SpotifyPlaylistSearchResult =>
        Boolean(playlist && typeof playlist === "object")
    );

    return playlists.filter((playlist) => playlist.public !== false);
  } catch (error) {
    console.error("Error searching Spotify playlists:", error);
    throw error;
  }
};

/**
 * Fetch detailed playlist information
 */
export const fetchPlaylistDetails = async (
  playlistId: string,
  options?: SpotifyRequestOptions
): Promise<SpotifyPlaylistDetails> => {
  try {
    return await spotifyFetch<SpotifyPlaylistDetails>(
      `/playlists/${playlistId}?market=US`,
      options
    );
  } catch (error) {
    console.error("Error fetching Spotify playlist details:", error);
    throw error;
  }
};

/**
 * Fetch Spotify playlist analytics (followers and tracks)
 */
export const fetchSpotifyPlaylistAnalytics = async (
  playlistUrl: string,
  clientId: string = spotifyConfig.clientId,
  clientSecret: string = spotifyConfig.clientSecret
): Promise<{ followers: number; trackCount: number } | null> => {
  try {
    const playlistId = extractPlaylistId(playlistUrl);
    if (!playlistId) {
      console.error("Could not extract playlist ID from URL:", playlistUrl);
      return null;
    }

    const details = await fetchPlaylistDetails(playlistId, {
      clientId,
      clientSecret,
    });

    return {
      followers: details.followers?.total ?? 0,
      trackCount: details.tracks?.total ?? 0,
    };
  } catch (error) {
    console.error("Error fetching Spotify playlist analytics:", error);
    return null;
  }
};

/**
 * Update Spotify playlist analytics in the outreach database
 */
export const updateSpotifyPlaylistAnalytics = async (
  playlistId: string,
  clientId: string = spotifyConfig.clientId,
  clientSecret: string = spotifyConfig.clientSecret
): Promise<boolean> => {
  try {
    const playlist = await getOutreachItem<SpotifyPlaylist>(
      OutreachCategory.SPOTIFY_PLAYLISTS,
      playlistId
    );

    if (!playlist || !playlist.playlistUrl) {
      console.error("Playlist not found or missing URL");
      return false;
    }

    const analytics = await fetchSpotifyPlaylistAnalytics(
      playlist.playlistUrl,
      clientId,
      clientSecret
    );

    if (!analytics) {
      console.error(
        "Failed to fetch analytics for playlist:",
        playlist.playlistName
      );
      return false;
    }

    await updateOutreachItem<SpotifyPlaylist>(
      OutreachCategory.SPOTIFY_PLAYLISTS,
      playlistId,
      {
        playlistFollowers: analytics.followers,
        trackCount: analytics.trackCount,
        lastUpdated: new Date().toISOString(),
      }
    );

    return true;
  } catch (error) {
    console.error("Error updating Spotify playlist analytics:", error);
    return false;
  }
};

export const __internal = {
  getSpotifyAccessToken,
  spotifyFetch,
};
